/**
 * @module components/ui/novaIcon/NovaIcon
 *
 * Default entry point for app UI icons: maps kit **`size`** to theme `icon.*` via
 * {@link NOVA_ICON_SIZES} and passes **`glyphSize`** into the glyph (triplet path selection).
 *
 * **Prefer this over raw Chakra `Icon` in feature code** so box size and glyph
 * kit stay aligned with the design system. Raw `Icon` is still appropriate inside individual
 * glyph implementations under `assets/icons`.
 *
 * Recommended usage:
 * `import { FilterIcon } from 'assets/icons/v2';`
 * `<NovaIcon icon={FilterIcon} size="m" color="icon.quaternary" aria-hidden />`
 */

import type { ElementType, JSX } from 'react';
import type { IconProps } from '@chakra-ui/react';

import {
  NOVA_ICON_SIZES,
  type NovaIconGlyphProps,
  type NovaIconGlyphSize,
  type NovaIconProps,
} from './novaIconTypes';

type GlyphComponent = ElementType<NovaIconGlyphProps>;

/** Widens the public union so we can destructure `icon` / `as` without narrowing pain. */
type NovaIconInternal = Omit<IconProps, 'boxSize' | 'as' | 'size'> & {
  size?: NovaIconGlyphSize;
  icon?: GlyphComponent;
  as?: GlyphComponent;
};

export const NovaIcon = (props: NovaIconProps): JSX.Element => {
  const { icon, as: asGlyph, size = 'm', ...glyphProps } = props as NovaIconInternal;
  const Glyph = icon ?? asGlyph;
  if (Glyph == null) {
    throw new Error('NovaIcon requires `icon` or `as`');
  }
  const boxSize = NOVA_ICON_SIZES[size];

  return (
    <Glyph flexShrink={0} alignSelf="center" boxSize={boxSize} glyphSize={size} {...glyphProps} />
  );
};

NovaIcon.displayName = 'NovaIcon';
